/**
 * Wrapper functions for fetch
 * May be called from any environment: browser, lambda, node
 * @module
 */
import { CORS_PROXY_REGEX, DEFAULT_REQUEST_TIMEOUT } from "common/constants";
import { type ApplicationError } from "../types/structuredErrorResponse";
import type { UserNotificationType } from "../types/userNotification";

export interface NotificationWithHTTPStatusCode extends UserNotificationType {
  statusCode: number;
}

export interface GenericErrorResponse {
  message: string;
}

export interface FetchErrorResponse extends NotificationWithHTTPStatusCode {
  message: string;
  messageType: "error";
  data?: ApplicationError;
}

/**
 * fetchWithTimeout calls fetch with a configurable timeout parameter
 */
export async function fetchWithTimeout(
  info: RequestInfo,
  init: RequestInit = {},
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT,
): Promise<Response> {
  let resp: Response;

  // AbortController is not available on older versions of node.js.
  if (typeof AbortController === "undefined") {
    resp = (await Promise.race([
      fetch(info, init),
      new Promise((_, reject) =>
        setTimeout(
          () => reject(new Error("The request to the servers timed out. Refresh the page and try again.")),
          timeoutMs,
        ),
      ),
    ])) as any;
  } else {
    const controller = new AbortController();
    const id = setTimeout(
      () => controller.abort("The request to the servers timed out. Refresh the page and try again."),
      timeoutMs,
    );
    resp = await fetch(info, {
      ...init,
      signal: controller.signal,
    });
    clearTimeout(id);
  }

  if (!resp.ok) {
    console.error(generateErrorMessageFromResponse(resp));
    throw resp;
  }

  return resp;
}

export async function fetchWithTimeoutAndRetry(
  info: RequestInfo,
  init?: RequestInit,
  retries: number = 3,
  timeoutMs: number = DEFAULT_REQUEST_TIMEOUT,
): Promise<Response> {
  let count = 0;
  while (true) {
    try {
      const resp = await fetchWithTimeout(info, init, timeoutMs);
      if (!resp.ok) {
        throwErrorFromResponse(resp);
      }
      return resp;
    } catch (e) {
      if (++count === retries) {
        throw e;
      }
      await waitForMs(getRandomIntInclusive(10, 30));
    }
  }
}

function generateErrorMessageFromResponse(resp: Response) {
  return `Request to "${resp.url}" failed. Status: ${resp.status}. StatusText: ${resp.statusText}.`;
}

function throwErrorFromResponse(resp: Response) {
  throw new Error(generateErrorMessageFromResponse(resp));
}

export function objectToQueryParams(obj: { [key in string]: any }, partialParams?: boolean): string {
  return (
    (partialParams ? "" : "?") +
    Object.keys(obj)
      .map((key) => {
        return encodeURIComponent(key) + "=" + encodeURIComponent(obj[key]);
      })
      .join("&")
  );
}

/**
 * @param error error message or object
 * @param errorMessageContextualPrefix contextual information prepended to error message
 * @returns
 */
export async function handleFetchError(
  error: string | Error | Response | GenericErrorResponse,
  errorMessageContextualPrefix?: string,
): Promise<FetchErrorResponse> {
  let message: string = "";
  let statusCode: number = 200;
  let data: ApplicationError | undefined;

  if (error instanceof Response) {
    statusCode = error.status;
    let jsonMessage: string | undefined;

    if (!error.bodyUsed) {
      try {
        const json = await error.json();
        data = json?.data;
        jsonMessage = json?.message;
      } catch (e) {
        console.error("Error parsing response body", e);
      }
    }

    if (jsonMessage) {
      message = jsonMessage;
    } else {
      switch (statusCode) {
        case 409: {
          message = "Conflict with existing resource.";
          break;
        }
        case 413: {
          message = "File too large to upload.";
          break;
        }
        case 429: {
          message = "Rate limit exceeded; try again later.";
          break;
        }
        default: {
          message = `An error occurred during the request. Please try again.`;
        }
      }
    }

    console.error(message);
  } else if (error instanceof Error) {
    message = error.toString();
    console.error(`${error} \n${error.stack}`);
  } else if (typeof error === "string") {
    message = error;
    console.error(error);
  } else if (error.message) {
    message = error.message;
  }

  if (message) {
    message = errorMessagePrependIfPrefix(message, errorMessageContextualPrefix);
    return { message, messageType: "error", statusCode: statusCode, data };
  } else {
    return { message: "Unknown error", messageType: "error", statusCode: statusCode, data };
  }
}

function errorMessagePrependIfPrefix(message: string, responseMessagePrefix: string | undefined) {
  message = responseMessagePrefix ? `${responseMessagePrefix}: ${message}` : message;
  return message;
}

// We need to use a proxy endpoint to get around CORS issues in Chrome with s3 urls
export function proxifyURL(url: string) {
  if (CORS_PROXY_REGEX.test(url)) {
    return location.origin + `/cors-proxy?url=${encodeURIComponent(url)}`;
  }

  return url;
}

export function memoProxyFetch<T>(f: (url: string) => Promise<T>) {
  const cache: { [f: string]: Promise<T> } = {};

  return (url: string) => {
    url = proxifyURL(url);

    if (cache[url] !== undefined) {
      return cache[url];
    }

    return (cache[url] = f(url));
  };
}

export function memoFetch<T>(f: (url: string) => Promise<T>) {
  const cache: { [f: string]: Promise<T> } = {};

  return (url: string) => {
    if (cache[url] !== undefined) {
      return cache[url];
    }
    return (cache[url] = f(url));
  };
}

export async function fetchArrayBufferWithRetry(url: string, retries: number) {
  let count = 0;
  while (true) {
    try {
      const resp = await fetch(url);
      if (!resp.ok) {
        throwErrorFromResponse(resp);
      }
      return await resp.arrayBuffer();
    } catch (e) {
      if (++count === retries) {
        throw e;
      }
      await waitForMs(getRandomIntInclusive(10, 30));
    }
  }
}

function waitForMs(milliseconds: number) {
  return new Promise<void>((resolve) => setTimeout(resolve, milliseconds));
}

function getRandomIntInclusive(min: number, max: number): number {
  min = Math.ceil(min);
  max = Math.floor(max);
  return Math.floor(Math.random() * (max - min + 1) + min); //The maximum is inclusive and the minimum is inclusive
}

export interface ExponentialRetryConfig {
  maxRetries?: number;
  baseDelayMs?: number;
  jitterPercent?: number; // 0-1, applied as ±
  timeoutMs?: number;
  shouldRetry?: (error: unknown) => boolean;
}

function calculateBackoffDelay(attempt: number, baseDelayMs: number, jitterPercent: number): number {
  const delay = baseDelayMs * Math.pow(2, attempt);
  const jitter = delay * jitterPercent * (Math.random() * 2 - 1);
  return delay + jitter;
}

/**
 * Default retry predicate: retries on network errors and 5xx/429 responses.
 * Does not retry on 4xx client errors (except 429 Too Many Requests).
 * Uses duck-typing for Response check to work across environments.
 */
function defaultShouldRetry(error: unknown): boolean {
  if (typeof error === "object" && error !== null && "status" in error) {
    const status = (error as { status: number }).status;
    return status >= 500 || status === 429;
  }
  // Network errors, timeouts, etc. — retry
  return true;
}

/**
 * Fetch with exponential backoff retry (1s → 2s → 4s with jitter)
 */
export async function fetchWithExponentialRetry(
  info: RequestInfo,
  init: RequestInit = {},
  config: ExponentialRetryConfig = {},
): Promise<Response> {
  const {
    maxRetries = 3,
    baseDelayMs = 1000,
    jitterPercent = 0.1,
    timeoutMs = DEFAULT_REQUEST_TIMEOUT,
    shouldRetry = defaultShouldRetry,
  } = config;

  for (let attempt = 0; attempt < maxRetries; attempt++) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort("Request timed out"), timeoutMs);

    try {
      const response = await fetch(info, {
        ...init,
        signal: controller.signal,
      });

      clearTimeout(timeoutId);

      if (!response.ok) {
        throw response;
      }

      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      if (!shouldRetry(error) || attempt === maxRetries - 1) {
        throw error;
      }
      await waitForMs(calculateBackoffDelay(attempt, baseDelayMs, jitterPercent));
    }
  }

  throw new Error("Unexpected: retry loop exited without returning or throwing");
}
